"use client";

import { useEffect, useRef } from "react";
import * as THREE from "three";
import { useTheme } from "../../Utils/useTheme";

export default function HeroInteractiveBg() {
  const containerRef = useRef(null);
  const { theme } = useTheme();

  const isGreen = theme === "green";
  const isLight = theme === "light";

  const orangeColor = isGreen ? 0x10b981 : (isLight ? 0xea580c : 0xf97316);

  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const width = rect.width || window.innerWidth;
    const height = rect.height || window.innerHeight;

    // 1. Scene Setup
    const scene = new THREE.Scene();

    // 2. Camera Setup
    const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 100);
    camera.position.z = 10;

    // 3. Renderer Setup
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      powerPreference: "high-performance",
    });
    renderer.setSize(width, height, false);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.domElement.style.position = "absolute";
    renderer.domElement.style.inset = "0";
    renderer.domElement.style.width = "100%";
    renderer.domElement.style.height = "100%";
    container.appendChild(renderer.domElement);

    // 4. Create Particles
    const particleGroup = new THREE.Group();
    scene.add(particleGroup);

    const particleCount = 200;
    const positions = new Float32Array(particleCount * 3);
    const velocities = [];

    // Spread particles randomly in space
    for (let i = 0; i < particleCount; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 20;     // x
      positions[i * 3 + 1] = (Math.random() - 0.5) * 14; // y
      positions[i * 3 + 2] = (Math.random() - 0.5) * 12; // z

      velocities.push({
        x: (Math.random() - 0.5) * 0.008,
        y: (Math.random() - 0.5) * 0.008,
        z: (Math.random() - 0.5) * 0.008,
      });
    }

    const particleGeo = new THREE.BufferGeometry();
    particleGeo.setAttribute("position", new THREE.BufferAttribute(positions, 3));

    // Particle material (soft glow)
    const particleMat = new THREE.PointsMaterial({
      color: orangeColor, // Dynamic color
      size: 0.07,
      transparent: true,
      opacity: 0.55,
      blending: THREE.AdditiveBlending,
    });

    const particles = new THREE.Points(particleGeo, particleMat);
    particleGroup.add(particles);

    // 5. Connective lines setup
    const maxConnections = 300;
    const linePositions = new Float32Array(maxConnections * 2 * 3);
    const lineColors = new Float32Array(maxConnections * 2 * 3);
    const lineGeo = new THREE.BufferGeometry();
    lineGeo.setAttribute("position", new THREE.BufferAttribute(linePositions, 3));
    lineGeo.setAttribute("color", new THREE.BufferAttribute(lineColors, 3));

    const lineMat = new THREE.LineBasicMaterial({
      vertexColors: true,
      transparent: true,
      opacity: 0.16,
      blending: THREE.AdditiveBlending,
      linewidth: 1,
    });
    const lineSegments = new THREE.LineSegments(lineGeo, lineMat);
    particleGroup.add(lineSegments);

    // 6. Interactive mouse move listeners
    let mouseX = 0;
    let mouseY = 0;
    const handleMouseMove = (e) => {
      mouseX = (e.clientX - window.innerWidth / 2) / (window.innerWidth / 2);
      mouseY = (e.clientY - window.innerHeight / 2) / (window.innerHeight / 2);
    };
    window.addEventListener("mousemove", handleMouseMove);

    // 7. Resize listener
    const handleResize = () => {
      if (!container) return;
      const r = container.getBoundingClientRect();
      const w = r.width || width;
      const h = r.height || height;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h, false);
    };
    window.addEventListener("resize", handleResize);

    // 8. Animation Loop
    let animFrameId;
    const animate = () => {
      animFrameId = requestAnimationFrame(animate);

      // Parallax effect on mouse movement
      particleGroup.rotation.y += 0.0006;
      particleGroup.rotation.x += 0.0003;
      particleGroup.position.x += (mouseX * 1.5 - particleGroup.position.x) * 0.03;
      particleGroup.position.y += (-mouseY * 1.2 - particleGroup.position.y) * 0.03;

      // Update positions
      const posAttr = particleGeo.getAttribute("position");
      const posArray = posAttr.array;

      for (let i = 0; i < particleCount; i++) {
        // Apply velocity
        posArray[i * 3] += velocities[i].x;
        posArray[i * 3 + 1] += velocities[i].y;
        posArray[i * 3 + 2] += velocities[i].z;

        // Boundary checks
        if (Math.abs(posArray[i * 3]) > 10) velocities[i].x *= -1;
        if (Math.abs(posArray[i * 3 + 1]) > 7) velocities[i].y *= -1;
        if (Math.abs(posArray[i * 3 + 2]) > 6) velocities[i].z *= -1;
      }
      posAttr.needsUpdate = true;

      // Update line connections based on distances
      let lineCount = 0;
      const linePosAttr = lineGeo.getAttribute("position");
      const lineColorAttr = lineGeo.getAttribute("color");

      for (let i = 0; i < particleCount; i++) {
        for (let j = i + 1; j < particleCount; j++) {
          if (lineCount >= maxConnections) break;

          const dx = posArray[i * 3] - posArray[j * 3];
          const dy = posArray[i * 3 + 1] - posArray[j * 3 + 1];
          const dz = posArray[i * 3 + 2] - posArray[j * 3 + 2];
          const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

          if (dist < 3.2) {
            const index = lineCount * 2;
            linePosAttr.setXYZ(index, posArray[i * 3], posArray[i * 3 + 1], posArray[i * 3 + 2]);
            linePosAttr.setXYZ(index + 1, posArray[j * 3], posArray[j * 3 + 1], posArray[j * 3 + 2]);

            // Draw line color fading with distance
            const alpha = 1.0 - dist / 3.2;
            lineColorAttr.setXYZ(index, 0.97 * alpha, 0.45 * alpha, 0.08 * alpha); // Brand orange
            lineColorAttr.setXYZ(index + 1, 0.98 * alpha, 0.75 * alpha, 0.15 * alpha); // Brand gold
            lineCount++;
          }
        }
      }
      linePosAttr.needsUpdate = true;
      lineColorAttr.needsUpdate = true;
      lineGeo.setDrawRange(0, lineCount * 2);

      renderer.render(scene, camera);
    };
    animate();

    // 9. Cleanup
    return () => {
      cancelAnimationFrame(animFrameId);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("resize", handleResize);
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }

      particleGeo.dispose();
      particleMat.dispose();
      lineGeo.dispose();
      lineMat.dispose();
      renderer.dispose();
    };
  }, [theme, orangeColor]);

  return <div ref={containerRef} className="absolute inset-0 w-full h-full z-0 overflow-hidden pointer-events-none" />;
}
